import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Camera, Video, Plus, Edit2, Trash2, Check, X, Eye, 
  RefreshCw, Settings, MapPin, Wifi, WifiOff, Globe, Server, Hash
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NetworkCameraSettings = () => {
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCamera, setEditingCamera] = useState(null);
  const [previewCamera, setPreviewCamera] = useState(null);

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch cameras
  const { data: cameras = [], isLoading } = useQuery({
    queryKey: ['cameras'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/cameras`, { headers });
      if (!res.ok) throw new Error('Failed to fetch cameras');
      return res.json();
    }
  });

  // Add camera
  const addMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/cameras`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to add camera');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['cameras']);
      setShowAddDialog(false);
      toast.success('Camera added');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update camera
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/api/cameras/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error('Failed to update camera');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['cameras']);
      setEditingCamera(null);
      toast.success('Camera updated');
    },
    onError: (error) => toast.error(error.message)
  });

  // Delete camera
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/cameras/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error('Failed to delete camera');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['cameras']);
      toast.success('Camera deleted');
    },
    onError: (error) => toast.error(error.message)
  });

  // Get stream info for preview
  const getStreamInfo = async (cameraId) => {
    try {
      const res = await fetch(`${API_URL}/api/cameras/${cameraId}/stream-info`, { headers });
      if (!res.ok) throw new Error('Failed to get stream info');
      const data = await res.json();
      setPreviewCamera(data);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const CameraForm = ({ camera, onSubmit, onCancel, isLoading }) => {
    const [formData, setFormData] = useState({
      name: camera?.name || '',
      location: camera?.location || 'main_gate',
      connection_type: camera?.connection_type || 'stream_url',
      stream_url: camera?.stream_url || '',
      stream_type: camera?.stream_type || 'mjpeg',
      ip_address: camera?.ip_address || '',
      ddns_host: camera?.ddns_host || '',
      port: camera?.port || 554,
      channel: camera?.channel || 1,
      serial_number: camera?.serial_number || '',
      manufacturer: camera?.manufacturer || '',
      model: camera?.model || '',
      username: camera?.username || '',
      password: '',
      is_active: camera?.is_active ?? true
    });

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(formData);
        }}
        className="space-y-4"
      >
        {/* Device Information */}
        <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
          <h4 className="font-medium text-sm flex items-center gap-2">
            <Camera className="h-4 w-4" />
            Device Information
          </h4>
          <div>
            <label className="text-sm font-medium">Device Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="Main Gate Camera"
              data-testid="input-camera-name"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Serial Number</label>
              <Input
                value={formData.serial_number}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                placeholder="SN-12345678"
                data-testid="input-serial-number"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Location</label>
              <Select 
                value={formData.location} 
                onValueChange={(value) => setFormData({ ...formData, location: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="main_gate">Main Gate</SelectItem>
                  <SelectItem value="north_gate">North Gate</SelectItem>
                  <SelectItem value="south_gate">South Gate</SelectItem>
                  <SelectItem value="parking_entrance">Parking Entrance</SelectItem>
                  <SelectItem value="parking_exit">Parking Exit</SelectItem>
                  <SelectItem value="reception">Reception</SelectItem>
                  <SelectItem value="loading_dock">Loading Dock</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Manufacturer</label>
              <Input
                value={formData.manufacturer}
                onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                placeholder="Hikvision, Dahua..."
              />
            </div>
            <div>
              <label className="text-sm font-medium">Model</label>
              <Input
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                placeholder="DS-2CD2143G0-I"
              />
            </div>
          </div>
        </div>

        {/* Connection Settings */}
        <div className="space-y-3 p-3 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-sm flex items-center gap-2">
            <Wifi className="h-4 w-4" />
            Connection Settings
          </h4>
          
          <Tabs value={formData.connection_type} onValueChange={(v) => setFormData({ ...formData, connection_type: v })}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="stream_url">
                <Globe className="h-4 w-4 mr-1" />
                Stream URL
              </TabsTrigger>
              <TabsTrigger value="ip_ddns">
                <Server className="h-4 w-4 mr-1" />
                IP / DDNS
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="stream_url" className="space-y-3 mt-3">
              <div>
                <label className="text-sm font-medium">Stream URL *</label>
                <Input
                  value={formData.stream_url}
                  onChange={(e) => setFormData({ ...formData, stream_url: e.target.value })}
                  placeholder="http://192.168.1.100:8080/video"
                  data-testid="input-stream-url"
                />
                <p className="text-xs text-gray-500 mt-1">
                  MJPEG: http://ip:port/video | RTSP: rtsp://ip:port/stream
                </p>
              </div>
            </TabsContent>
            
            <TabsContent value="ip_ddns" className="space-y-3 mt-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">IP Address</label>
                  <Input
                    value={formData.ip_address}
                    onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                    placeholder="192.168.1.100"
                    data-testid="input-ip-address"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">DDNS Host</label>
                  <Input
                    value={formData.ddns_host}
                    onChange={(e) => setFormData({ ...formData, ddns_host: e.target.value })}
                    placeholder="mycamera.ddns.net"
                    data-testid="input-ddns-host"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Port</label>
                  <Input
                    type="number"
                    value={formData.port}
                    onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) || 554 })}
                    placeholder="554"
                    data-testid="input-port"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Channel</label>
                  <Input
                    type="number"
                    value={formData.channel}
                    onChange={(e) => setFormData({ ...formData, channel: parseInt(e.target.value) || 1 })}
                    placeholder="1"
                    min="1"
                    data-testid="input-channel"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Use IP for local network cameras. Use DDNS for remote access.
              </p>
            </TabsContent>
          </Tabs>
          
          <div>
            <label className="text-sm font-medium">Stream Type</label>
            <Select 
              value={formData.stream_type} 
              onValueChange={(value) => setFormData({ ...formData, stream_type: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mjpeg">MJPEG (HTTP)</SelectItem>
                <SelectItem value="rtsp">RTSP</SelectItem>
                <SelectItem value="http">HTTP Snapshot</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              MJPEG works directly in browser. RTSP requires VLC or similar.
            </p>
          </div>
        </div>

        {/* Authentication */}
        <div className="space-y-3 p-3 bg-amber-50 rounded-lg">
          <h4 className="font-medium text-sm flex items-center gap-2">
            <Hash className="h-4 w-4" />
            Authentication (Optional)
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Username</label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="admin"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Password</label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={camera ? '(unchanged)' : 'password'}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            checked={formData.is_active}
            onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
          />
          <label className="text-sm">Camera is active</label>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={isLoading} data-testid="submit-camera-btn">
            {isLoading ? 'Saving...' : camera ? 'Update Camera' : 'Add Camera'}
          </Button>
        </div>
      </form>
    );
  };

  return (
    <div className="space-y-6" data-testid="network-camera-settings">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Network Cameras</h2>
          <p className="text-sm text-gray-500">Configure WiFi/IP cameras for gate monitoring</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-camera-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Camera
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading cameras...</div>
      ) : cameras.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Camera className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">No cameras configured</h3>
            <p className="text-sm text-gray-500 mb-4">
              Add your network cameras to view live feeds at gate stations
            </p>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Camera
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cameras.map((camera) => (
            <Card key={camera.id} data-testid={`camera-card-${camera.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Video className="h-5 w-5 text-gray-500" />
                    <CardTitle className="text-base">{camera.name}</CardTitle>
                  </div>
                  <Badge variant={camera.is_active ? 'default' : 'secondary'}>
                    {camera.is_active ? (
                      <><Wifi className="h-3 w-3 mr-1" /> Active</>
                    ) : (
                      <><WifiOff className="h-3 w-3 mr-1" /> Inactive</>
                    )}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <MapPin className="h-4 w-4" />
                  <span className="capitalize">{camera.location?.replace('_', ' ')}</span>
                </div>
                <div className="text-xs font-mono text-gray-400 truncate">
                  {camera.stream_url}
                </div>
                <div className="text-xs">
                  <Badge variant="outline">{camera.stream_type?.toUpperCase()}</Badge>
                </div>

                {/* Preview area */}
                {camera.is_active && camera.stream_type === 'mjpeg' && (
                  <div className="relative bg-gray-900 rounded overflow-hidden h-32">
                    <img
                      src={camera.stream_url}
                      alt={camera.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="absolute inset-0 hidden items-center justify-center text-gray-400 text-sm">
                      <Camera className="h-6 w-6 mr-2" />
                      Connection failed
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => getStreamInfo(camera.id)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Preview
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setEditingCamera(camera)}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => {
                      if (confirm('Delete this camera?')) {
                        deleteMutation.mutate(camera.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Camera Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Network Camera</DialogTitle>
          </DialogHeader>
          <CameraForm
            onSubmit={(data) => addMutation.mutate(data)}
            onCancel={() => setShowAddDialog(false)}
            isLoading={addMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Camera Dialog */}
      <Dialog open={!!editingCamera} onOpenChange={(open) => !open && setEditingCamera(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Camera</DialogTitle>
          </DialogHeader>
          {editingCamera && (
            <CameraForm
              camera={editingCamera}
              onSubmit={(data) => updateMutation.mutate({ id: editingCamera.id, data })}
              onCancel={() => setEditingCamera(null)}
              isLoading={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewCamera} onOpenChange={(open) => !open && setPreviewCamera(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewCamera?.name}</DialogTitle>
          </DialogHeader>
          {previewCamera && (
            <div className="space-y-4">
              <div className="text-sm text-gray-500">
                <p>Location: {previewCamera.location}</p>
                <p>Type: {previewCamera.stream_type?.toUpperCase()}</p>
              </div>
              
              {previewCamera.stream_type === 'mjpeg' ? (
                <div className="bg-black rounded-lg overflow-hidden">
                  <img
                    src={previewCamera.stream_url}
                    alt={previewCamera.name}
                    className="w-full"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y=".9em" font-size="90">📷</text></svg>';
                    }}
                  />
                </div>
              ) : (
                <div className="bg-gray-900 rounded-lg p-8 text-center">
                  <Video className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-300 mb-2">RTSP Stream</p>
                  <p className="text-sm text-gray-500 mb-4">
                    Open in VLC or compatible player:
                  </p>
                  <code className="block bg-gray-800 text-green-400 p-3 rounded text-sm break-all">
                    {previewCamera.stream_url}
                  </code>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Help Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Camera Setup Help</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-3">
          <div>
            <strong>MJPEG Cameras (Recommended)</strong>
            <p className="text-gray-500">
              Most IP cameras support MJPEG over HTTP. URL format: <code className="bg-gray-100 px-1 rounded">http://IP:PORT/video</code>
            </p>
          </div>
          <div>
            <strong>RTSP Cameras</strong>
            <p className="text-gray-500">
              For RTSP streams, use VLC or similar software. URL format: <code className="bg-gray-100 px-1 rounded">rtsp://IP:PORT/stream</code>
            </p>
          </div>
          <div>
            <strong>Common IP Camera Ports</strong>
            <p className="text-gray-500">
              HTTP: 80, 8080 | RTSP: 554, 8554
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NetworkCameraSettings;
